import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useDispatch, useSelector } from "react-redux";
import {
  getPlatformsByAccount,
  createPlatformThunk,
  updatePlatformThunk,
  removePlatform,
} from "../../features/platforms/platformsSlice";
import { toast } from "react-toastify";
import { confirmationModal } from "../../constants/confirmation.constants";
import { Icons } from "../../constants/icon-library";
import { formatPlatformName, PLATFORMS_ENUM } from "../../constants/constants";

const NewPlatform = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editUuid = searchParams.get("edit");
  const { platforms = [] } = useSelector((state) => state.platforms);
  const { user } = useSelector((state) => state.user);
  const originalPlatform = useRef(null);

  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");

  const [authUrl, setAuthUrl] = useState("");
  const [tokenUrl, setTokenUrl] = useState("");
  const [profileUrl, setProfileUrl] = useState("");
  const [scope, setScope] = useState("");
  const [isConfigRequired, setConfigRequired] = useState(false);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (user?.org) {
      dispatch(getPlatformsByAccount(user.org));
    }
  }, [user, dispatch]);

  useEffect(() => {
    if (editUuid && platforms.length) {
      const platform = platforms.find((p) => p.uuid === editUuid);
      if (platform) {
        setName(platform.name || "");
        setType(platform.type || "");
        setClientId(platform.clientId || "");
        setClientSecret(platform.clientSecret || "");

        setAuthUrl(platform.authUrl || "");
        setTokenUrl(platform.tokenUrl || "");
        setProfileUrl(platform.profileUrl || "");
        setScope(platform.scope || "");
        setConfigRequired(!!platform.isConfigRequired);

        setIsEditMode(true);
        originalPlatform.current = {
          name: platform.name || "",
          type: platform.type || "",
          clientId: platform.clientId || "",
          clientSecret: platform.clientSecret || "",
          authUrl: platform.authUrl || "",
          tokenUrl: platform.tokenUrl || "",
          profileUrl: platform.profileUrl || "",
          scope: platform.scope || "",
          isConfigRequired: !!platform.isConfigRequired,
        };
      }
    } else {
      setName("");
      setType("");
      setClientId("");
      setClientSecret("");
      setAuthUrl("");
      setTokenUrl("");
      setScope("");
      setProfileUrl("");
      setIsEditMode(false);
      originalPlatform.current = null;
    }
  }, [editUuid, platforms]);

  useEffect(() => {
    if (!isEditMode) {
      setClientId("");
      setClientSecret("");
      setAuthUrl("");
      setTokenUrl("");
      setScope("");
      setProfileUrl("");
    }
  }, [isEditMode]);

  const handleCancel = () => {
    navigate(-1);
  };

  const buildChangedData = () => {
    const changed = { uuid: editUuid };
    const orig = originalPlatform.current || {};
    let hasChange = false;

    const normalize = (v) => (typeof v === "string" ? v.trim() : v);

    const candidates = [
      {
        key: "name",
        val: normalize(name),
        orig: orig.name,
      },
      { key: "type", val: normalize(type), orig: orig.type },
      { key: "clientId", val: normalize(clientId), orig: orig.clientId },
      {
        key: "clientSecret",
        val: normalize(clientSecret),
        orig: orig.clientSecret,
      },
      { key: "authUrl", val: normalize(authUrl), orig: orig.authUrl },
      { key: "tokenUrl", val: normalize(tokenUrl), orig: orig.tokenUrl },
      { key: "profileUrl", val: normalize(profileUrl), orig: orig.profileUrl },
      { key: "scope", val: normalize(scope), orig: orig.scope },
      {
        key: "isConfigRequired",
        val: isConfigRequired,
        orig: orig.isConfigRequired,
      },
    ];
    candidates[0].val = candidates[0].val || "";

    candidates.forEach(({ key, val, orig }) => {
      if ((orig || "") !== (val || "")) {
        changed[key] = val;
        hasChange = true;
      }
    });
    if ("accountId" in orig && orig.accountId !== (user.org || "")) {
      changed.accountId = user.org;
      hasChange = true;
    }
    changed.accountId = user.org;

    return hasChange ? changed : null;
  };

  const handleSubmit = async () => {
    if (isEditMode) {
      const changedData = buildChangedData();
      if (!changedData || Object.keys(changedData).length <= 1) {
        toast.info("No changes to update.");
        return;
      }
      try {
        await dispatch(updatePlatformThunk(changedData)).unwrap();
        toast.success("Platform updated successfully.");
        navigate(`/platform`);
      } catch (err) {
        toast.error("Failed to update platform.");
      }
    } else {
      const platformData = {
        uuid: uuidv4(),
        name: name.trim() || "",
        type: type.trim(),
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        authUrl: authUrl.trim(),
        tokenUrl: tokenUrl.trim(),
        scope: scope.trim(),
        profileUrl: profileUrl.trim(),
        accountId: user.org,
      };
      try {
        await dispatch(createPlatformThunk(platformData)).unwrap();
        toast.success("Platform saved successfully.");
        navigate(`/platform`);
      } catch (err) {
        toast.error("Failed to save platform.");
      }
    }
  };

  const handleDelete = async () => {
    const result = await confirmationModal({
      title: "Are you sure?",
      text: "This will permanently delete the platform account.",
    });

    if (result.isConfirmed) {
      try {
        await dispatch(removePlatform(editUuid)).unwrap();
        toast.success("Platform deleted successfully.");
        navigate(`/platform`);
      } catch (err) {
        toast.error("Failed to delete platform.");
      }
    }
  };

  const shouldShowField = () => {
    return type === "other" || isEditMode;
  };

  const shouldShowAdvancedTab = () => {
    return type && type !== "other" && !isEditMode;
  };

  return (
    <div className="content">
      <div className="pt-5 d-flex flex-column">
        <div className="mb-2">
          <h3>{isEditMode ? "Edit" : "Add New"} Platform</h3>
        </div>

        <div className="pt-2 px-0">
          <>
            <div className="mb-4">
              <label className="fw-bold mb-2">
                Platform Type <span className="text-danger">*</span>
              </label>
              <select
                className="form-control"
                value={type}
                onChange={(e) => setType(e.target.value)}
                disabled={isEditMode}
              >
                <option value="">Select a platform type</option>
                {PLATFORMS_ENUM.map((platform) => (
                  <option key={platform} value={platform}>
                    {formatPlatformName(platform)}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="fw-bold mb-2">
                Platform Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter account name"
              />
            </div>

            {shouldShowAdvancedTab() && (
              <div className="mb-4">
                <button
                  type="button"
                  className="btn btn-link p-0 text-decoration-none"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                >
                  <i
                    className={`wb ${Icons(
                      showAdvanced ? "chevron-down" : "chevron-right",
                    )} me-2`}
                  ></i>
                  Advanced Settings
                </button>
              </div>
            )}

            {(shouldShowField() || showAdvanced) && (
              <>
                <div className="mb-4">
                  <label className="fw-bold mb-2">Client ID</label>
                  <input
                    type="text"
                    className="form-control"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    placeholder="Enter client ID"
                  />
                </div>

                <div className="mb-4">
                  <label className="fw-bold mb-2">Client Secret</label>
                  <input
                    type="text"
                    className="form-control"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    placeholder="Enter client secret"
                  />
                </div>

                <div className="mb-4">
                  <label className="fw-bold mb-2">Authorization URL</label>
                  <input
                    type="text"
                    className="form-control"
                    value={authUrl}
                    onChange={(e) => setAuthUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </div>

                <div className="mb-4">
                  <label className="fw-bold mb-2">Token URL</label>
                  <input
                    type="text"
                    className="form-control"
                    value={tokenUrl}
                    onChange={(e) => setTokenUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </div>
                <div className="mb-4">
                  <label className="fw-bold mb-2">Profile URL</label>
                  <input
                    type="text"
                    className="form-control"
                    value={profileUrl}
                    onChange={(e) => setProfileUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </div>

                <div className="mb-4">
                  <label className="fw-bold mb-2">Scope</label>
                  <input
                    type="text"
                    className="form-control"
                    value={scope}
                    onChange={(e) => setScope(e.target.value)}
                    placeholder="Enter scope (optional)"
                  />
                </div>
                <div className="mb-4">
                  <label className="fw-bold mb-2 d-block">
                    Project Required <span className="text-danger">*</span>
                  </label>

                  <div className="form-check d-flex align-items-center ps-0 ms-1">
                    <input
                      type="checkbox"
                      className="form-check-input me-2 mt-0"
                      id="isConfigRequired"
                      checked={isConfigRequired}
                      onChange={(e) => setConfigRequired(e.target.checked)}
                    />
                    <label
                      className="form-check-label mb-0"
                      htmlFor="isConfigRequired"
                    >
                      Require workspace/project selection for this platform
                    </label>
                  </div>
                </div>
                {/* Redirect URI block removed */}
              </>
            )}
          </>
        </div>

        <div className={`d-flex justify-content-between border-0 px-0 pb-0`}>
          {isEditMode ? (
            <button
              className="btn btn-sm btn-outline-danger px-3"
              title="Delete"
              onClick={() => handleDelete(editUuid)}
            >
              <i
                className={`wb ${Icons("delete")} fs-8 me-2 mt-3 align-middle`}
              ></i>
              Delete
            </button>
          ) : (
            <button
              type="button"
              onClick={handleCancel}
              className="btn d-flex align-items-center btn-outline-danger mb-2"
            >
              <i className={`wb ${Icons("cancel")} fs-8 me-2 `} />
              Cancel
            </button>
          )}
          <button
            className="btn btn-primary me-2 align-self-end"
            onClick={handleSubmit}
          >
            <i
              className={`wb ${Icons("save")} fs-8 me-2 mt-3 align-middle`}
            ></i>
            {isEditMode ? "Update" : "Save"} Platform
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewPlatform;
